import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { AppBottomSheet, Button, SearchableSelect, Text } from '~/components/core';
import { Feather, Ionicons } from '~/components/core/Icons';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { NONE_SENTINEL, RuleState } from '../../utils/types';
import { CreateStrategyWizardApi } from '../../hooks/useCreateStrategyWizard';
import NodeAttributeForm from './NodeAttributeForm';

type Props = {
    visible: boolean;
    // The rule being edited (copied into a local draft on open) and its 0-based display index.
    initialRule: RuleState;
    ruleIndex: number;
    onSave: (rule: RuleState) => void;
    onCancel: () => void;
    wizard: Pick<
        CreateStrategyWizardApi,
        | 'optionsAtSlot'
        | 'comboForCodes'
        | 'seedNode'
        | 'instanceLabel'
        | 'instanceDescription'
        | 'paramFields'
        | 'describeConnection'
        | 'violationsFor'
    >;
};

// The one secondary surface open above the editor. Both are Modals of their own, so they are
// tracked as a single value rather than two booleans — that makes them mutually exclusive by
// construction and rules out presenting one while the other is still dismissing (an iOS
// nested-Modal hazard).
type Overlay = { kind: 'picker'; slot: number } | { kind: 'params'; code: string } | null;

/**
 * Full-screen editor for one rule: a cascade of indicator selects that resolve a combination of
 * any length (single-indicator combos included via the "— none —" sentinel), the resulting
 * conditions as read-only sentences, and per-indicator parameters one cog-tap away in a sheet
 * (defaults pre-filled — the optimizer tunes ranges, so the default path needs no editing).
 * Saving is gated on a complete combination with no constraint violations.
 */
export default function RuleEditor({ visible, initialRule, ruleIndex, onSave, onCancel, wizard }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [draft, setDraft] = useState<RuleState>(initialRule);
    const [overlay, setOverlay] = useState<Overlay>(null);
    const pickerSlot = overlay?.kind === 'picker' ? overlay.slot : null;
    const paramsCode = overlay?.kind === 'params' ? overlay.code : null;

    useEffect(() => {
        if (visible) {
            setDraft(initialRule);
        }
        // Never leave a sheet or picker behind when the editor itself opens or closes.
        setOverlay(null);
    }, [visible, initialRule]);

    // A blank rule opens straight into the picker. On onShow, not on mount: presenting a nested
    // Modal while this one is still animating in is the iOS hazard `overlay` guards against.
    const openPickerIfBlank = () => {
        if (initialRule.codes.length === 0) {
            setOverlay({ kind: 'picker', slot: 0 });
        }
    };

    const combination = useMemo(() => wizard.comboForCodes(draft.codes), [wizard, draft.codes]);
    const violations = useMemo(() => wizard.violationsFor(draft), [wizard, draft]);
    // The same check narrowed to the indicator whose sheet is open, by validating a one-code
    // rule — the sheet can only edit that node, so nothing else can be at fault.
    const paramViolations = useMemo(() => {
        if (!paramsCode) {
            return [];
        }
        const node = draft.nodes[paramsCode];
        return node ? wizard.violationsFor({ codes: [paramsCode], nodes: { [paramsCode]: node }, editOpen: {} }) : [];
    }, [wizard, paramsCode, draft.nodes]);
    const complete = !!combination && draft.codes.length > 0 && draft.codes.every((c) => !!draft.nodes[c]);

    // The picker slots to render: slot 0 always, then each further slot for which the previous one
    // is filled and at least one indicator option remains.
    const slots = useMemo(() => {
        const out = [0];
        let slot = 1;
        while (draft.codes.length >= slot && wizard.optionsAtSlot(draft.codes, slot).length > 0) {
            out.push(slot);
            slot += 1;
        }
        return out;
    }, [draft.codes, wizard]);

    // Pick (or clear) the indicator in slot `slot`. Picking drops the later slots, seeds the new
    // node, then greedily auto-extends with the first option at each subsequent slot so the common
    // case resolves a complete combination in one tap. NONE_SENTINEL stops the cascade here,
    // finalizing the smaller combination that matches the selection so far.
    const pickSlot = (slot: number, value: string) => {
        let codes: string[];
        if (value === NONE_SENTINEL) {
            codes = draft.codes.slice(0, slot);
        } else if (!value) {
            return;
        } else {
            codes = [...draft.codes.slice(0, slot), value];
            for (
                let opts = wizard.optionsAtSlot(codes, codes.length);
                opts.length > 0;
                opts = wizard.optionsAtSlot(codes, codes.length)
            ) {
                codes = [...codes, opts[0]];
            }
        }
        const nodes: Record<string, Record<string, unknown>> = {};
        const editOpen: Record<string, boolean> = {};
        for (const code of codes) {
            const node = draft.nodes[code] ?? wizard.seedNode(code);
            if (node) {
                nodes[code] = node;
            }
            if (draft.editOpen[code]) {
                editOpen[code] = true;
            }
        }
        setDraft({ codes, nodes, editOpen });
    };

    const pickerOptions = useMemo(() => {
        if (pickerSlot === null) {
            return [];
        }
        // Sorted by label rather than left in backend order: with every indicator of the side on
        // offer the list is long, and A–Z is the only order a reader can predict.
        const options = wizard
            .optionsAtSlot(draft.codes, pickerSlot)
            .map((code) => ({
                value: code,
                label: wizard.instanceLabel(code),
                sublabel: wizard.instanceDescription(code) || undefined,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        // Offer "— none —" when the selection so far is already a complete combination but larger
        // ones also contain it (e.g. BOLL + Price, which extends to a four-indicator combination).
        const showNone = pickerSlot > 0 && !!wizard.comboForCodes(draft.codes.slice(0, pickerSlot));
        return showNone ? [{ value: NONE_SENTINEL, label: i18n.t('ai-strategy-rule-none') }, ...options] : options;
    }, [pickerSlot, draft.codes, wizard]);

    return (
        <Modal visible={visible} animationType="slide" onRequestClose={onCancel} onShow={openPickerIfBlank}>
            <View style={styles.container}>
                <View style={styles.headerRow}>
                    <Text fontType="LATO_BOLD" style={styles.title}>
                        {`${i18n.t('ai-strategy-setup-rule')} ${ruleIndex + 1}`}
                    </Text>
                    <Pressable hitSlop={8} onPress={onCancel} accessibilityLabel="Close">
                        <Ionicons name="close" size={scaleSize(22)} style={styles.closeIcon} />
                    </Pressable>
                </View>

                <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                    {slots.map((slot) => {
                        const code = draft.codes[slot];
                        const instance = code ? wizard.instanceLabel(code) : null;
                        const hasVisibleParams = !!code && wizard.paramFields(code).some((f) => !f.hidden);
                        return (
                            <React.Fragment key={`slot-${slot}`}>
                                <Text style={styles.fieldLabel}>{`${i18n.t('ai-strategy-rule-indicator')} ${
                                    slot + 1
                                }`}</Text>
                                <View style={styles.selectRow}>
                                    <Pressable
                                        style={styles.selectField}
                                        onPress={() => setOverlay({ kind: 'picker', slot })}
                                    >
                                        <Text
                                            noTranslate={!!code}
                                            style={StyleSheet.flatten([
                                                styles.selectValue,
                                                !code ? styles.selectPlaceholder : null,
                                            ])}
                                        >
                                            {instance ?? 'ai-strategy-rule-pick-indicator'}
                                        </Text>
                                        <Feather
                                            name="chevron-down"
                                            size={scaleSize(16)}
                                            style={styles.selectChevron}
                                        />
                                    </Pressable>
                                    {code && hasVisibleParams ? (
                                        <Pressable
                                            hitSlop={8}
                                            style={styles.paramsButton}
                                            onPress={() => setOverlay({ kind: 'params', code })}
                                            accessibilityLabel={`${instance} parameters`}
                                        >
                                            <Ionicons
                                                name="settings-outline"
                                                size={scaleSize(18)}
                                                style={styles.paramsIcon}
                                            />
                                        </Pressable>
                                    ) : null}
                                </View>
                            </React.Fragment>
                        );
                    })}

                    {combination ? (
                        <>
                            <Text style={styles.fieldLabel}>ai-strategy-rule-conditions</Text>
                            {combination.connections.map((conn, i) => (
                                <Text noTranslate key={`conn-${i}`} style={styles.connectionText}>
                                    {wizard.describeConnection(conn)}
                                </Text>
                            ))}
                        </>
                    ) : null}

                    {violations.map((v, i) => (
                        <Text noTranslate key={`violation-${i}`} style={styles.violationText}>
                            {v}
                        </Text>
                    ))}
                </ScrollView>

                <View style={styles.footer}>
                    <View style={styles.footerButton}>
                        <Button
                            title="cancel"
                            onPress={onCancel}
                            backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                            titleColor={theme.text}
                        />
                    </View>
                    <View style={styles.footerButton}>
                        <Button
                            title="ai-strategy-rule-save"
                            onPress={() => onSave(draft)}
                            disabled={!complete || violations.length > 0}
                        />
                    </View>
                </View>

                <SearchableSelect
                    visible={pickerSlot !== null}
                    onClose={() => setOverlay(null)}
                    title="ai-strategy-rule-pick-indicator"
                    options={pickerOptions}
                    selectedValue={pickerSlot !== null ? draft.codes[pickerSlot] : undefined}
                    onSelect={(value) => {
                        if (pickerSlot !== null) {
                            pickSlot(pickerSlot, value);
                        }
                    }}
                />

                <AppBottomSheet
                    visible={paramsCode !== null}
                    onClose={() => setOverlay(null)}
                    title={paramsCode ? wizard.instanceLabel(paramsCode) : undefined}
                    titleNoTranslate
                >
                    {paramsCode ? (
                        <>
                            <Text style={styles.paramsHint}>ai-strategy-rule-params-hint</Text>
                            <NodeAttributeForm
                                attrs={wizard.paramFields(paramsCode)}
                                value={draft.nodes[paramsCode] ?? {}}
                                onChange={(v) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        nodes: { ...prev.nodes, [paramsCode]: v },
                                    }))
                                }
                            />
                            {/* Scoped to the open indicator: the rule-wide list at the bottom of the
                                editor is hidden behind the sheet while it is up. */}
                            {paramViolations.map((v, i) => (
                                <Text noTranslate key={`param-violation-${i}`} style={styles.violationText}>
                                    {v}
                                </Text>
                            ))}
                        </>
                    ) : null}
                </AppBottomSheet>
            </View>
        </Modal>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: theme.background,
        },
        headerRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: scaleSize(16),
            paddingTop: scaleSize(16),
            paddingBottom: scaleSize(8),
        },
        title: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
        },
        closeIcon: {
            color: theme.textSecondary,
        },
        body: {
            flex: 1,
        },
        bodyContent: {
            paddingHorizontal: scaleSize(16),
            paddingBottom: scaleSize(16),
        },
        fieldLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(12),
            marginBottom: scaleSize(6),
        },
        selectRow: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: scaleSize(8),
            marginBottom: scaleSize(8),
        },
        selectField: {
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(10),
        },
        paramsButton: {
            padding: scaleSize(6),
        },
        paramsIcon: {
            color: theme.textSecondary,
        },
        paramsHint: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginBottom: scaleSize(12),
        },
        selectValue: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        selectPlaceholder: {
            color: theme.textSecondary,
        },
        selectChevron: {
            color: theme.textSecondary,
        },
        connectionText: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(6),
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(8),
            marginBottom: scaleSize(6),
        },
        violationText: {
            fontSize: FONT_SIZE_XS,
            color: theme.alert,
            marginTop: scaleSize(4),
        },
        footer: {
            flexDirection: 'row',
            padding: scaleSize(12),
            borderTopWidth: StyleSheet.hairlineWidth,
            borderTopColor: theme.tabIconDefault,
        },
        footerButton: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
    });
